import React from 'react';
import PropTypes from 'prop-types';

import { Spinner } from '@common/components/Spinner';
import FormSteps from '@common/components/FormSteps';
import {
  FormWrapper,
  Header,
  FormStep,
  Modal,
  MobileHeading,
  CloseModalButton,
  PrevStepButton,
  InnerScroller,
} from '@common/components/BookingForms';
import { SignInModalForm } from '@common/features/SignIn';
import GuestDetailsStep from '@routes/SpaClasses/components/GuestDetailsStep';

const STICKY_BAR_ID = 'classes-details-sticky-bar';

class SpaClassesConfigurationWizard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSignInModalOpen: false,
      loginError: '',
    };

    this.closeModal = this.closeModal.bind(this);
    this.getFormSteps = this.getFormSteps.bind(this);
    this.handleSignIn = this.handleSignIn.bind(this);
    this.handleSignInModalOpen = this.handleSignInModalOpen.bind(this);
  }

  componentWillUnmount() {
    const { destroyForm } = this.props;
    destroyForm('classConfigurationForm');
  }

  getFormSteps() {
    const {
      existingGuests,
      datePartyData,
      selectedTimeslot: {
        numberOfSpacesAvailable,
        gender,
        spaClass,
        price,
        discount,
        maxBookingsPerClient,
      },
      handleBasketUpdate,
      closeConfiguration,
      showGenderNeutral,
    } = this.props;

    const { isSignInModalOpen } = this.state;

    const finishWizardAction = (data) => {
      handleBasketUpdate(data).then(() => closeConfiguration());
    };

    return [
      <FormStep isNarrow noGutter key={0}>
        <GuestDetailsStep
          stickyBarId={STICKY_BAR_ID}
          initialValues={{
            guests: new Array(1).fill({ gender: gender || null }),
          }}
          totalGuestAmount={maxBookingsPerClient ?? numberOfSpacesAvailable}
          basketGuests={existingGuests}
          datePartyData={datePartyData}
          isGenderSpecific={gender}
          spaClass={spaClass}
          timeSlotPrice={price}
          onSubmit={finishWizardAction}
          isSignInModalOpen={isSignInModalOpen}
          handleSignInModalOpen={this.handleSignInModalOpen}
          showGenderNeutral={showGenderNeutral}
          discount={discount}
        />
      </FormStep>,
    ];
  }

  handleSignInModalClose = () => {
    this.setState({
      isSignInModalOpen: false,
    });
  };

  handleSignInModalOpen() {
    this.setState({
      isSignInModalOpen: true,
    });
  }

  closeModal() {
    const { destroyForm, closeConfiguration } = this.props;

    closeConfiguration();
    destroyForm('classConfigurationForm');
  }

  async handleSignIn(data) {
    const { property, signIn } = this.props;

    try {
      await signIn(property.id, data);
      this.setState({
        isSignInModalOpen: false,
      });
    } catch (e) {
      this.setState({
        loginError: 'invalidLogin',
      });
    }
  }

  render() {
    const {
      selectedTimeslot: { spaClass },
      isFetching,
    } = this.props;

    const { isSignInModalOpen, loginError } = this.state;

    const formSteps = this.getFormSteps();

    return (
      <>
        <SignInModalForm
          isModalOpen={isSignInModalOpen}
          handleModalClose={this.handleSignInModalClose}
          onSubmit={this.handleSignIn}
          loginError={loginError}
        />
        <Modal isVisible>
          <Spinner isActive={isFetching} showDelayMessage />
          <Header>
            <MobileHeading>
              <PrevStepButton onClick={this.goToPreviousStep} />
              {spaClass.name}
            </MobileHeading>
            <CloseModalButton onClick={this.closeModal} />
          </Header>
          <InnerScroller isModalOpen>
            <FormWrapper>
              <FormSteps activeIndex={0}>{formSteps}</FormSteps>
            </FormWrapper>
          </InnerScroller>
        </Modal>
      </>
    );
  }
}

SpaClassesConfigurationWizard.propTypes = {
  datePartyData: PropTypes.shape({
    adults: PropTypes.number,
    children: PropTypes.number,
  }).isRequired,
  destroyForm: PropTypes.func.isRequired,
  room: PropTypes.shape({
    roomId: PropTypes.number,
    title: PropTypes.string,
    rates: PropTypes.array,
  }).isRequired,
  closeConfiguration: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  existingGuests: PropTypes.arrayOf(PropTypes.object).isRequired,
  basketDetails: PropTypes.shape({
    items: PropTypes.array,
    total: PropTypes.object,
  }).isRequired,
  selectedTimeslot: PropTypes.shape({
    numberOfSpacesAvailable: PropTypes.number,
    maxBookingsPerClient: PropTypes.number,
    gender: PropTypes.string,
    spaClass: PropTypes.object,
    price: PropTypes.object,
  }).isRequired,
  handleBasketUpdate: PropTypes.func.isRequired,
  property: PropTypes.object.isRequired,
  signIn: PropTypes.func.isRequired,
};

export default SpaClassesConfigurationWizard;
